/* eslint-disable no-console */
import {
  EyeOutlined,
  RedoOutlined,
  SearchOutlined,
  UploadOutlined
} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {
  Button,
  Flex,
  Input,
  Select,
  Table,
  TableProps,
  Tag,
  Tooltip
} from 'antd';
import {ColumnType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useState, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';

import DocumentUploadDrawerForm from '../../../components/DrawerForm/Utils/DocumentUploadDrawer';
import ESGDocumentManagerForm from '../../../components/Forms/ESGDocumentManagerForm';
import {filterSelectOptions} from '../../../components/Forms/Utils/commonFunctions';
import Loader from '../../../components/Loader/Loader';
import ActionsButton from '../../../components/Table/Utils/ActionsButton';
import TableBulkSelectionBar from '../../../components/Table/Utils/TableBulkSelectionBar';
import {useAIDocumentOptions} from '../../../hooks/useAIDocumentOptions';
import {useGlobalYear} from '../../../hooks/useGlobalYear';
import {financialYear} from '../../../interface/Units';
import {
  AIDocument,
  AIDocumentStatus,
  aiDocumentRecord,
  apiErrorMessage,
  deleteAIDocument,
  getAIDocumentFileUrl,
  listAIDocuments,
  retryAIDocument
} from '../../../services/modules/AIDocumentManager/aiDocumentsAPI';
import {hasUserRole, UserRole} from '../../../utils/commonFunctions';
import {yearSelect} from '../../../utils/FormUtils/yearSelect';
import showMessage from '../../../utils/showMessage';
import {shouldBlockReadOnly} from '../../../utils/SubscriptionHelper';
import {StepState} from '../Constants/commonConstants';

/** `_id` keeps the shared table helpers (selection, ActionsButton) working. */
type DocumentType = AIDocument & {_id: string};

const STATUS_COLORS: Record<AIDocumentStatus, string> = {
  queued: 'processing',
  running: 'processing',
  succeeded: 'success',
  failed: 'error'
};

const isInProgress = (doc: DocumentType): boolean =>
  doc.status === 'queued' || doc.status === 'running';

const DEFAULT_REFRESH_SECONDS = 5;

const ESGDocument: React.FC = () => {
  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [refresh, setRefresh] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const {selectedYear, setSelectedYear} = useGlobalYear();
  const [searchParams, setSearchParams] = useSearchParams();
  // The AI document API only accepts "2026-2027" style years.
  const firstYear = yearSelect(financialYear.AprMar);
  const [dataSource, setDataSource] = useState<DocumentType[]>([]);
  const [frameworkType, setFrameworkType] = useState<any>('BRSR');
  const [documentType, setDocumentType] = useState<string | undefined>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const searchInput = useRef<InputRef>(null);
  const {options, frameworkOptions, documentTypeOptions} =
    useAIDocumentOptions();
  const isMaker =
    hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const filteredDocuments = documentTypeOptions(frameworkType);

  const fetchDocuments = async (): Promise<DocumentType[]> => {
    const docs = await listAIDocuments({
      year: selectedYear,
      framework: frameworkType,
      documentType
    });

    return docs.map((doc) => ({...doc, _id: doc.id}));
  };

  const SearchFilter = (
    title: string,
    dataIndex: keyof DocumentType
  ): ColumnType<DocumentType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return (
          record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase()) ?? false
        );
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const handleView = (record: DocumentType) => {
    getAIDocumentFileUrl(record.id)
      .then((url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
      })
      .catch((err) => {
        showMessage({
          status: 'error',
          message: apiErrorMessage(err, 'Unable to open the file')
        });
      });
  };

  const handleRetry = (record: DocumentType) => {
    retryAIDocument(record.id)
      .then(() => {
        showMessage({status: 'success', message: 'Reading the document again'});
        setRefresh(true);
      })
      .catch((err) => {
        showMessage({
          status: 'error',
          message: apiErrorMessage(err, 'Unable to retry this document')
        });
      });
  };

  const columns: TableProps<DocumentType>['columns'] = [
    {
      title: 'Sr No',
      dataIndex: '',
      render: (value: any, record: any, index: number) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      }
    },
    {
      title: 'File name',
      dataIndex: 'file_name',
      ...SearchFilter('File name', 'file_name')
    },
    {
      title: 'Document Type',
      dataIndex: 'document_type',
      filters:
        documentType === 'all'
          ? Array.from(
              new Map(
                dataSource.map((doc) => [
                  doc.document_type,
                  doc.document_type_label ?? doc.document_type
                ])
              )
            ).map(([value, text]) => ({text, value}))
          : undefined,
      onFilter: (value, record) => {
        if (documentType !== 'all') {
          return true;
        }

        return record.document_type === value;
      },
      render: (value, record) => record.document_type_label ?? value
    },
    {
      title: 'BRSR Section',
      dataIndex: 'brsr_section',
      render: (value, record) =>
        [value, record.brsr_principle].filter(Boolean).join(' · ') || '-'
    },
    {
      title: 'Uploaded at',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (value) => moment(value).format('DD/MM/YYYY hh:mm A')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value: AIDocumentStatus, record) => {
        const tag = (
          <Tag color={STATUS_COLORS[value]}>
            {record.status_label ?? options?.statuses?.[value] ?? value}
          </Tag>
        );

        return value === 'failed' && record.error ? (
          <Tooltip title={record.error}>{tag}</Tooltip>
        ) : (
          tag
        );
      }
    },
    {
      title: 'Action',
      dataIndex: 'operation',
      fixed: 'right',
      width: '200px !important',
      render: (value: any, record: DocumentType) => (
        <Flex align="center">
          <Tooltip title="View file">
            <Button
              shape="circle"
              type="link"
              style={{marginRight: '20px', backgroundColor: '#F5FBF3'}}
              icon={<EyeOutlined />}
              onClick={() => {
                handleView(record);
              }}
            />
          </Tooltip>
          {isMaker && record.status === 'failed' && !shouldBlockReadOnly() && (
            <Tooltip title="Retry reading">
              <Button
                shape="circle"
                type="link"
                style={{marginRight: '20px', backgroundColor: '#F5FBF3'}}
                icon={<RedoOutlined />}
                onClick={() => {
                  handleRetry(record);
                }}
              />
            </Tooltip>
          )}
          {isMaker && (
            <ActionsButton
              record={record}
              setShowModel={setShowModel}
              setEditData={(row: DocumentType) => {
                setEditData({
                  _id: row.id,
                  year: row.year,
                  bill_type: frameworkType,
                  doc_type: row.document_type,
                  file_name: row.file_name
                });
              }}
              setIsEdit={setIsEdit}
              handleDelete={handleDelete}
              noDelete={false}
              isConfigurator={true}
            />
          )}
        </Flex>
      )
    }
  ];

  useEffect(() => {
    const urlFrameworkType = searchParams.get('frameworkType');
    if (urlFrameworkType) {
      setFrameworkType(urlFrameworkType);
    }

    setDocumentType(searchParams.get('frameworkDocumentType') ?? 'all');
    setRefresh(true);
  }, []);

  // Drop a document type from the URL that the configured list no longer has.
  useEffect(() => {
    if (
      options &&
      documentType &&
      documentType !== 'all' &&
      !filteredDocuments.some((doc) => doc.value === documentType)
    ) {
      setDocumentType('all');
      handleSearch({name: 'frameworkDocumentType', value: 'all'});
      setRefresh(true);
    }
  }, [options, frameworkType, documentType]);

  useEffect(() => {
    if (refresh) {
      if (selectedYear) {
        fetchDocuments()
          .then((res) => {
            setPagination((prev) => ({
              ...prev,
              current: 1
            }));
            setDataSource(res);
            setRefresh(false);
          })
          .catch((err) => {
            console.log('ai documents err:', err);
            setRefresh(false);
          });
      } else {
        setRefresh(false);
      }
    }
  }, [refresh]);

  // Re-read the list quietly while any document is still being read.
  useEffect(() => {
    if (refresh || !dataSource.some(isInProgress)) {
      return;
    }

    const seconds =
      options?.display?.refresh_seconds ?? DEFAULT_REFRESH_SECONDS;
    const timer = setTimeout(() => {
      fetchDocuments()
        .then((res) => {
          setDataSource(res);
        })
        .catch(() => {});
    }, seconds * 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [dataSource, refresh, options]);

  const handleSearch = (event: {name: string; value: string}) => {
    const {name, value} = event;
    if (value) {
      searchParams.set(name, value);
      setSearchParams(searchParams);
    } else {
      searchParams.delete(name);
      setSearchParams(searchParams);
    }
  };

  const handleDelete = (_id: string[] | string) => {
    const ids = Array.isArray(_id) ? _id : [_id];

    Promise.allSettled(ids.map(deleteAIDocument))
      .then((results) => {
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length) {
          showMessage({
            status: 'error',
            message: `Unable to delete ${failed.length} of ${ids.length} documents`
          });
        } else {
          showMessage({status: 'success', message: 'Document deleted'});
        }
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  const selectedIds = (
    selectedRowKeys as Array<string | null | undefined>
  ).filter((k): k is string => Boolean(k));
  const selectedCount = selectedIds.length;

  return (
    <>
      <Flex
        vertical
        style={{padding: 10, background: '#fff', borderRadius: 10}}
      >
        <Flex justify="space-between" align="center" style={{marginBottom: 20}}>
          <Flex gap={15}>
            <Select
              style={{width: '120px'}}
              placeholder="Year"
              value={selectedYear}
              options={firstYear}
              size="middle"
              popupMatchSelectWidth={false}
              onChange={(e) => {
                setSelectedYear(e);
                setRefresh(true);
                handleSearch({
                  name: 'selectedYear',
                  value: e
                });
              }}
            />

            <Select
              showSearch
              placeholder="Framework"
              value={frameworkType}
              style={{minWidth: '150px'}}
              defaultValue={''}
              disabled={true}
              options={frameworkOptions}
              popupMatchSelectWidth={false}
              size="middle"
              onChange={(e) => {
                setFrameworkType(e);
                handleSearch({
                  name: 'frameworkType',
                  value: e
                });
                setRefresh(true);
              }}
              filterOption={filterSelectOptions}
            />
            <Select
              showSearch
              placeholder="Document Type"
              value={documentType}
              style={{minWidth: '200px'}}
              defaultValue={'all'}
              options={[
                ...filteredDocuments,
                {
                  label: 'All Docs',
                  value: 'all'
                }
              ]}
              popupMatchSelectWidth={false}
              size="middle"
              onChange={(e) => {
                setDocumentType(e);
                setRefresh(true);
                handleSearch({
                  name: 'frameworkDocumentType',
                  value: e
                });
              }}
              filterOption={filterSelectOptions}
            />
          </Flex>
          <Flex gap={15}>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              style={{background: '#3e5c3a'}}
              onClick={() => {
                setIsEdit(false);
                setShowModel(true);
              }}
            >
              Upload Document
            </Button>
          </Flex>
        </Flex>

        {selectedCount > 0 && !shouldBlockReadOnly() && (
          <TableBulkSelectionBar
            selectedCount={selectedCount}
            selectedIds={selectedIds}
            handleDelete={handleDelete}
            onSelectAll={
              !hasUserRole(UserRole.View_Only) ? handleSelectAll : undefined
            }
            onDeselectAll={() => {
              setSelectedRowKeys([]);
            }}
            allSelectableCount={dataSource.length}
          />
        )}

        {/* Table */}
        <Table
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (
              selectedKey: React.Key[],
              selectedRow: DocumentType[]
            ) => {
              setSelectedRowKeys(
                selectedRow.map((item) => {
                  return item._id;
                })
              );
            }
          }}
          dataSource={dataSource}
          columns={columns}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize
          }}
          rowKey="_id"
          onChange={(page: any, _filters: any, _sorter: any, extra: any) => {
            if (extra?.action === 'filter') {
              setPagination((prev) => ({
                ...prev,
                current: 1,
                pageSize: page?.pageSize ?? prev.pageSize
              }));
            } else {
              setPagination({
                current: page.current,
                pageSize: page.pageSize
              });
            }
          }}
          loading={
            refresh ?? {
              indicator: <Loader />
            }
          }
        />
      </Flex>
      {showModel && (
        <DocumentUploadDrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          API={aiDocumentRecord}
          setRefresh={setRefresh}
          data={isEdit ? editData : undefined}
          hasOCRProcessing={false}
          FormComponent={<ESGDocumentManagerForm isEdit={isEdit} />}
          stepsTitle={isEdit ? ['Details'] : ['Details', 'Upload Document']}
          stepsType={
            isEdit
              ? [StepState.NORMAL]
              : [StepState.NORMAL, StepState.UPLOAD_AND_VALIDATE]
          }
        />
      )}
    </>
  );
};

export default ESGDocument;
